import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SendIcon from "@mui/icons-material/Send";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  getSettingsGroups,
  generateGroupExcelBase64,
  GroupExcelResult,
  SCREEN_SHEET_KEY,
  mapScreenData,
} from "../../actions/ImportExcelDataMapper";
import { importExcelData } from "../../services/requests";
import { getProductType } from "../../../constants/login";
import { getExcelReviewMode } from "../../../excel/review/mode";
import {
  getOrderedImportPlan,
  getMinImportOrderForWorksheetNames,
} from "../../../excel/import/orderedPlan";
import { generateSheetConfigExcelBase64 } from "../../../excel/export/workbook";
import { getExcelExportSheets } from "../../../excel/sheetRegistry";
import { mapWorksheetToExportRows } from "../../../excel/mapping/payloadRows";
import { removeSampleTestFromPayload } from "../../../excel/mapping/sampleDataFilter";
import {
  enrichSpaServicesExportPayload,
  validateSpaServiceImportReferences,
} from "../../../excel/mapping/serviceDetailRows";
import { validateRateTypeExportDuplicates } from "../../../excel/mapping/golfRateTypeRows";
import { buildPerSheetImportFileName } from "../../../excel/import/moduleNames";
import { GolfPropertyEnum } from "../../../constants/NavbarList";

const findSheetConfigForItem = (item: ExcelReviewItem, productId: number) =>
  getExcelExportSheets(productId).find(
    (config) =>
      item.screens.length > 0 &&
      item.screens.every((screen) =>
        config.worksheets.some(
          (worksheet) => worksheet.worksheetName === screen,
        ),
      ),
  );

/** Rebuild import JSON + xlsx from current onboarding payload (unless user uploaded a file). */
const resolveImportResult = async (
  item: ExcelReviewItem,
  exportPayload: Record<string, unknown>,
  productId: number,
): Promise<GroupExcelResult | null> => {
  if (!item.result) {
    return null;
  }
  if (item.uploadedFileName) {
    return item.result;
  }

  const config = findSheetConfigForItem(item, productId);
  if (!config) {
    return item.result;
  }

  return (
    (await generateSheetConfigExcelBase64(config, exportPayload)) ?? item.result
  );
};

const SERVICE_DETAIL_SCREENS = new Set([
  "ServiceDetail",
  "ServicePriceType",
  "ServiceEquipment",
  "ServiceSeasonalDate",
  "ServiceAvailableDays",
]);

const SERVICE_DETAIL_MASTER_SCREENS = ["ServiceGroups", "PriceTypes"];

const RATE_TYPE_SCREENS = new Set(["RateType"]);

const itemIncludesRateType = (item: ExcelReviewItem): boolean =>
  item.screens.some((screen) => RATE_TYPE_SCREENS.has(screen));

const itemIncludesServiceDetail = (item: ExcelReviewItem): boolean =>
  item.screens.some((screen) => SERVICE_DETAIL_SCREENS.has(screen));

const getMissingImportedMasterScreens = (
  item: ExcelReviewItem,
  allItems: ExcelReviewItem[],
  importedScreensThisBatch: ReadonlySet<string> = new Set(),
): string[] =>
  SERVICE_DETAIL_MASTER_SCREENS.filter(
    (screen) =>
      !importedScreensThisBatch.has(screen) &&
      !allItems.some(
        (candidate) =>
          candidate.isImported && candidate.activeScreens.includes(screen),
      ),
  );

const formatSpaInvalidReferenceError = (message: string): string | null => {
  const serviceGroupMatch = message.match(
    /INVALID\s+ServiceGroupCode\s+-DATA\s+-(\S+)/i,
  );
  if (serviceGroupMatch) {
    return `Service group code "${serviceGroupMatch[1]}" is not on the Visual One server yet. Import ServiceGroups successfully before ServiceDetail, then retry.`;
  }

  const priceTypeMatch = message.match(
    /INVALID\s+PriceTypeCode\s+-DATA\s+-(\S+)/i,
  );
  if (priceTypeMatch) {
    return `Price type code "${priceTypeMatch[1]}" is not on the Visual One server yet. Import PriceTypes successfully before ServiceDetail, then retry.`;
  }

  return null;
};

interface ExcelReviewItem {
  groupKey: string;
  screens: string[]; // all screens in group
  activeScreens: string[]; // screens that actually have data
  result: GroupExcelResult | null;
  isReviewed: boolean;
  isSelected: boolean;
  isImported: boolean;
  isPartialImport: boolean;
  isImporting: boolean;
  importMessage?: string;
  uploadedFileName?: string;
}

interface Props {
  payload: Record<string, unknown>;
  tenantId: number;
  propertyId: number;
  productId: number;
}

const ExcelReview: React.FC<Props> = ({
  payload,
  tenantId,
  propertyId,
  productId,
}) => {
  const reviewMode = getExcelReviewMode(productId);
  const productLabel = getProductType(productId) ?? "Product";
  const [items, setItems] = useState<ExcelReviewItem[]>([]);
  const [generating, setGenerating] = useState(true);
  const [importing, setImporting] = useState(false);
  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning";
  }>({
    open: false,
    message: "",
    severity: "success",
  });
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const generateAll = useCallback(async () => {
    setGenerating(true);
    const exportPayload = removeSampleTestFromPayload(payload);
    enrichSpaServicesExportPayload(exportPayload);
    const mode = reviewMode;

    if (mode === "sheet-by-sheet") {
      const importPlan = getOrderedImportPlan(productId);
      const minImportOrderByFile = new Map<string, number>();
      for (const entry of importPlan) {
        const fileKey = `${entry.payloadKey}::${entry.fileName}`;
        const current = minImportOrderByFile.get(fileKey);
        if (current === undefined || entry.importOrder < current) {
          minImportOrderByFile.set(fileKey, entry.importOrder);
        }
      }

      const sheetConfigs = getExcelExportSheets(productId);
      const generated: ExcelReviewItem[] = await Promise.all(
        sheetConfigs.map(async (config) => {
          let result: GroupExcelResult | null = null;
          try {
            result = await generateSheetConfigExcelBase64(
              config,
              exportPayload,
            );
          } catch {
            /* skip */
          }

          const activeScreens = config.worksheets
            .filter((worksheet) => {
              try {
                const rows = mapWorksheetToExportRows(
                  exportPayload[config.payloadKey],
                  worksheet,
                );
                return (
                  worksheet.templateOnly ||
                  worksheet.includeWhenEmpty ||
                  rows.length > 0
                );
              } catch {
                return false;
              }
            })
            .map((worksheet) => worksheet.worksheetName);

          const fileKey = `${config.payloadKey}::${config.fileName}`;
          const orderLabel = String(
            minImportOrderByFile.get(fileKey) ?? 999,
          ).padStart(3, "0");
          const sheetNames = config.worksheets
            .map((worksheet) => worksheet.worksheetName)
            .join(", ");
          const groupLabel =
            config.worksheets.length > 1
              ? `${config.fileName} (${sheetNames})`
              : sheetNames || config.fileName;

          return {
            groupKey: `${orderLabel} · ${groupLabel}`,
            screens: config.worksheets.map(
              (worksheet) => worksheet.worksheetName,
            ),
            activeScreens,
            result,
            isReviewed: false,
            isSelected: false,
            isImported: false,
            isPartialImport: false,
            isImporting: false,
          };
        }),
      );
      generated.sort((leftItem, rightItem) => {
        const orderA = Number.parseInt(leftItem.groupKey.split(" · ")[0], 10);
        const orderB = Number.parseInt(rightItem.groupKey.split(" · ")[0], 10);
        const safeA = Number.isFinite(orderA) ? orderA : 999;
        const safeB = Number.isFinite(orderB) ? orderB : 999;
        if (safeA !== safeB) {
          return safeA - safeB;
        }
        return leftItem.groupKey.localeCompare(rightItem.groupKey);
      });
      setItems(generated);
      setGenerating(false);
      return;
    }

    const settingsGroups = getSettingsGroups(productId);
    const generated: ExcelReviewItem[] = await Promise.all(
      settingsGroups.map(async (group) => {
        let result: GroupExcelResult | null = null;
        try {
          result = await generateGroupExcelBase64(group, exportPayload);
        } catch {
          /* skip */
        }
        const activeScreens = group.screens.filter((screenName) => {
          if (!SCREEN_SHEET_KEY[screenName]) return false;
          try {
            const rows = mapScreenData(screenName, exportPayload[screenName]);
            return rows.length > 0;
          } catch {
            return false;
          }
        });
        return {
          groupKey: group.label,
          screens: group.screens,
          activeScreens,
          result,
          isReviewed: false,
          isSelected: false,
          isImported: false,
          isPartialImport: false,
          isImporting: false,
        };
      }),
    );
    setItems(generated);
    setGenerating(false);
  }, [payload, productId, reviewMode]);

  useEffect(() => {
    generateAll();
  }, [generateAll]);

  const downloadAll = () => {
    items.forEach((item) => {
      if (item.result) downloadItem(item);
    });
  };

  const downloadItem = (item: ExcelReviewItem) => {
    if (!item.result) return;
    const link = document.createElement("a");
    link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${item.result.base64}`;
    link.download = item.result.fileName;
    link.click();
  };

  const toggleReviewed = (groupKey: string) => {
    setItems((prev) =>
      prev.map((it) =>
        it.groupKey === groupKey
          ? {
              ...it,
              isReviewed: !it.isReviewed,
              isSelected: it.isReviewed ? false : it.isSelected,
            }
          : it,
      ),
    );
  };

  const allReviewed = items
    .filter((it) => it.result)
    .every((it) => it.isReviewed);
  const allSelected = items
    .filter((it) => it.isReviewed && it.result)
    .every((it) => it.isSelected);
  const anyReviewable = items.some((it) => it.result);

  const toggleMarkAll = () => {
    setItems((prev) =>
      prev.map((it) =>
        it.result
          ? {
              ...it,
              isReviewed: !allReviewed,
              isSelected: allReviewed ? false : it.isSelected,
            }
          : it,
      ),
    );
  };

  const toggleSelectAll = () => {
    setItems((prev) =>
      prev.map((it) =>
        it.isReviewed && it.result ? { ...it, isSelected: !allSelected } : it,
      ),
    );
  };

  const toggleSelect = (groupKey: string) => {
    setItems((prev) =>
      prev.map((it) =>
        it.groupKey === groupKey && it.isReviewed
          ? { ...it, isSelected: !it.isSelected }
          : it,
      ),
    );
  };

  const handleUpload = (groupKey: string, file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const arrayBuffer = e.target?.result as ArrayBuffer;
      const bytes = new Uint8Array(arrayBuffer);
      let binary = "";
      bytes.forEach((b) => {
        binary += String.fromCharCode(b);
      });
      const base64 = btoa(binary);
      setItems((prev) =>
        prev.map((it) => {
          if (it.groupKey !== groupKey || !it.result) return it;
          return {
            ...it,
            result: { ...it.result, base64 },
            uploadedFileName: file.name,
          };
        }),
      );
    };
    reader.readAsArrayBuffer(file);
  };

  const importSelected = async () => {
    const selected = items.filter((it) => it.isSelected && it.result);
    if (selected.length === 0) return;
    setImporting(true);
    try {
      const importPayload = removeSampleTestFromPayload(payload);
      enrichSpaServicesExportPayload(importPayload);
      const sortedSelected = [...selected].sort(
        (leftItem, rightItem) =>
          getMinImportOrderForWorksheetNames(
            productId,
            leftItem.activeScreens,
          ) -
          getMinImportOrderForWorksheetNames(
            productId,
            rightItem.activeScreens,
          ),
      );

      let successCount = 0;
      let partialCount = 0;
      let lastImportError: string | null = null;
      let lastPartialMessage: string | null = null;
      const importedScreensThisBatch = new Set<string>();

      for (const item of sortedSelected) {
        setItems((prev) =>
          prev.map((it) =>
            it.groupKey === item.groupKey ? { ...it, isImporting: true } : it,
          ),
        );
        try {
          const importResult = await resolveImportResult(
            item,
            importPayload,
            productId,
          );
          if (!importResult?.dataUtilityImportData?.length) {
            throw new Error(
              `No import rows were generated for ${item.groupKey}. Confirm onboarding data is complete, then refresh Excel Review.`,
            );
          }

          if (item.uploadedFileName) {
            throw new Error(
              `Remove the uploaded file on ${item.groupKey} and refresh Excel Review so import uses the latest export mapping.`,
            );
          }

          if (itemIncludesServiceDetail(item)) {
            const missingMasters = getMissingImportedMasterScreens(
              item,
              items,
              importedScreensThisBatch,
            );
            if (missingMasters.length > 0) {
              throw new Error(
                `Import ${missingMasters.join(", ")} successfully before ${item.groupKey}. Visual One rejects service codes that are not on the server yet (INVALID … -DATA).`,
              );
            }

            const referenceErrors =
              validateSpaServiceImportReferences(importPayload);
            if (referenceErrors.length > 0) {
              throw new Error(referenceErrors[0]);
            }
          }

          if (itemIncludesRateType(item)) {
            const duplicateRateTypes = validateRateTypeExportDuplicates(
              importPayload[GolfPropertyEnum.rateTypes],
            );
            if (duplicateRateTypes.length > 0) {
              throw new Error(duplicateRateTypes[0]);
            }
          }

          const orderedSheets = [...importResult.dataUtilityImportData].sort(
            (leftSheet, rightSheet) =>
              getMinImportOrderForWorksheetNames(productId, [
                leftSheet.excelSheetName,
              ]) -
              getMinImportOrderForWorksheetNames(productId, [
                rightSheet.excelSheetName,
              ]),
          );

          const partialMessages: string[] = [];
          for (const sheetImportData of orderedSheets) {
            // Empty tabs must not fire ImportExcelData.
            if (
              typeof sheetImportData.totalRows === "number" &&
              sheetImportData.totalRows <= 0
            ) {
              continue;
            }

            const sheetFileName = buildPerSheetImportFileName(
              importResult.fileName,
              sheetImportData.excelSheetName,
            );
            const sheetOutcome = await importExcelData(
              tenantId,
              propertyId,
              productId,
              sheetFileName,
              [sheetImportData],
              importResult.base64,
            );
            if (sheetOutcome.partial) {
              partialMessages.push(
                sheetOutcome.message?.trim() ||
                  `Partial import for ${sheetImportData.excelSheetName}`,
              );
            }
          }

          if (partialMessages.length > 0) {
            const partialMessage = partialMessages.join(" | ");
            setItems((prev) =>
              prev.map((it) =>
                it.groupKey === item.groupKey
                  ? {
                      ...it,
                      isImported: false,
                      isPartialImport: true,
                      isImporting: false,
                      isSelected: false,
                      importMessage: partialMessage,
                    }
                  : it,
              ),
            );
            lastPartialMessage = partialMessage;
            partialCount++;
          } else {
            setItems((prev) =>
              prev.map((it) =>
                it.groupKey === item.groupKey
                  ? {
                      ...it,
                      isImported: true,
                      isPartialImport: false,
                      isImporting: false,
                      isSelected: false,
                      importMessage: undefined,
                    }
                  : it,
              ),
            );
            for (const screen of item.activeScreens) {
              importedScreensThisBatch.add(screen);
            }
            successCount++;
          }
        } catch (err) {
          setItems((prev) =>
            prev.map((it) =>
              it.groupKey === item.groupKey
                ? {
                    ...it,
                    isImporting: false,
                    isImported: false,
                    isPartialImport: false,
                  }
                : it,
            ),
          );
          const detail =
            err instanceof Error
              ? err.message
              : `Failed to import ${item.groupKey}`;
          lastImportError = formatSpaInvalidReferenceError(detail) ?? detail;
        }
      }
      if (lastImportError) {
        setSnack({ open: true, message: lastImportError, severity: "error" });
      } else if (lastPartialMessage) {
        setSnack({
          open: true,
          message:
            partialCount > 1
              ? `${partialCount} file(s) partially imported. ${lastPartialMessage}`
              : lastPartialMessage,
          severity: "warning",
        });
      } else if (successCount > 0) {
        setSnack({
          open: true,
          message: `Successfully imported ${successCount} file(s)`,
          severity: "success",
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Import failed";
      setSnack({ open: true, message, severity: "error" });
    }
    setImporting(false);
  };

  const selectedCount = items.filter((it) => it.isSelected).length;
  const reviewableCount = items.filter((it) => it.result).length;
  const reviewedCount = items.filter((it) => it.isReviewed && it.result).length;

  const getSelectDisabledReason = (item: ExcelReviewItem): string => {
    if (!item.result) return "No data — fill onboarding screens first";
    if (!item.isReviewed) return "Mark as reviewed before selecting";
    return "";
  };

  if (generating) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
          py: 6,
        }}
      >
        <CircularProgress />
        <Typography>Generating Excel files…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6" fontWeight={600}>
          Excel Review — {productLabel}
          {reviewMode === "sheet-by-sheet" ? " (sheet by sheet)" : ""}
        </Typography>
        <Stack direction="row" gap={1}>
          <Button
            startIcon={<RefreshIcon />}
            variant="outlined"
            size="small"
            onClick={generateAll}
            disabled={generating || importing}
          >
            Regenerate
          </Button>
          <Button
            startIcon={<SendIcon />}
            variant="contained"
            size="small"
            color="success"
            onClick={importSelected}
            disabled={selectedCount === 0 || importing}
          >
            {importing ? "Importing…" : `Import Selected (${selectedCount})`}
          </Button>
        </Stack>
      </Stack>

      {reviewableCount === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No Excel files could be generated. Add data on the onboarding screens,
          then click Regenerate.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          Review workflow: download each file → click the checkmark to mark as
          reviewed → select the row → Import Selected.
          {reviewedCount === 0 && selectedCount === 0 && (
            <>
              {" "}
              ({reviewableCount} file{reviewableCount === 1 ? "" : "s"} ready —
              none reviewed yet)
            </>
          )}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell padding="checkbox">
                <Tooltip
                  title={allSelected ? "Deselect All" : "Select All Reviewed"}
                >
                  <span>
                    <Checkbox
                      size="small"
                      checked={allSelected}
                      disabled={!items.some((it) => it.isReviewed && it.result)}
                      onChange={toggleSelectAll}
                    />
                  </span>
                </Tooltip>
              </TableCell>
              <TableCell>
                <strong>
                  {reviewMode === "sheet-by-sheet" ? "Sheet" : "Group"}
                </strong>
              </TableCell>
              <TableCell>
                <strong>
                  {reviewMode === "sheet-by-sheet" ? "Phase" : "Screens"}
                </strong>
              </TableCell>
              <TableCell>
                <strong>File</strong>
              </TableCell>
              <TableCell>
                <strong>Status</strong>
              </TableCell>
              <TableCell align="center">
                <Stack
                  direction="row"
                  gap={0.5}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Tooltip title="Download All">
                    <span>
                      <IconButton
                        size="small"
                        disabled={!anyReviewable}
                        onClick={downloadAll}
                        color="primary"
                      >
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip
                    title={
                      allReviewed
                        ? "Unmark All Reviewed"
                        : "Mark All as Reviewed"
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={!anyReviewable}
                        onClick={toggleMarkAll}
                        color={allReviewed ? "success" : "default"}
                      >
                        <CheckCircleIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 0.5 }}
                  >
                    Actions
                  </Typography>
                </Stack>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow
                key={item.groupKey}
                sx={{ opacity: !item.result ? 0.5 : 1 }}
              >
                <TableCell padding="checkbox">
                  <Tooltip title={getSelectDisabledReason(item)}>
                    <span>
                      <Checkbox
                        checked={item.isSelected}
                        disabled={!item.isReviewed || !item.result}
                        onChange={() => toggleSelect(item.groupKey)}
                        size="small"
                      />
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {item.groupKey}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {item.activeScreens.length > 0
                      ? item.activeScreens.join(", ")
                      : reviewMode === "sheet-by-sheet"
                        ? item.screens.join(", ")
                        : "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {item.uploadedFileName
                      ? `📎 ${item.uploadedFileName}`
                      : (item.result?.fileName ?? "—")}
                  </Typography>
                </TableCell>
                <TableCell>
                  {item.isImported ? (
                    <Chip label="Imported" color="success" size="small" />
                  ) : item.isPartialImport ? (
                    <Tooltip title={item.importMessage || "Partial import"}>
                      <Chip
                        label="Partial Import"
                        size="small"
                        color="warning"
                      />
                    </Tooltip>
                  ) : item.isImporting ? (
                    <Chip
                      label="Importing…"
                      color="default"
                      size="small"
                      icon={<CircularProgress size={10} />}
                    />
                  ) : item.isReviewed ? (
                    <Chip label="Reviewed" color="primary" size="small" />
                  ) : item.result ? (
                    <Chip
                      label="Pending Review"
                      size="small"
                      sx={{
                        bgcolor: "#FBC02D",
                        color: "rgba(0, 0, 0, 0.87)",
                      }}
                    />
                  ) : (
                    <Chip label="No Data" color="default" size="small" />
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" gap={0.5} justifyContent="center">
                    <Tooltip title="Download">
                      <span>
                        <IconButton
                          size="small"
                          disabled={!item.result}
                          onClick={() => downloadItem(item)}
                          color="primary"
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={
                        item.isReviewed
                          ? "Unmark as Reviewed"
                          : "Mark as Reviewed"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={!item.result}
                          onClick={() => toggleReviewed(item.groupKey)}
                          color={item.isReviewed ? "success" : "default"}
                        >
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Upload replacement Excel">
                      <span>
                        <IconButton
                          size="small"
                          disabled={!item.result}
                          onClick={() =>
                            fileInputRefs.current[item.groupKey]?.click()
                          }
                          color="warning"
                        >
                          <UploadFileIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <input
                      type="file"
                      accept=".xlsx"
                      style={{ display: "none" }}
                      ref={(el) => {
                        fileInputRefs.current[item.groupKey] = el;
                      }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleUpload(item.groupKey, file);
                        e.target.value = "";
                      }}
                    />
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snack.open}
        autoHideDuration={5000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} variant="filled">
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExcelReview;
